import type { CloudEnvironment, InfraOperation, HostGroupMember, DeploymentModel } from '@/core/infra_types'

export type StageStatus = 'completed' | 'in_progress' | 'failed' | 'not_started'

export interface LifecycleStage {
  id: 'created' | 'infrastructure' | 'runtime' | 'connected'
  status: StageStatus
  operationId?: string
  summary?: string
}

const INFRA_OP_TYPES = ['terraform_apply', 'provision']
const HELM_OP_TYPES = ['helm_install', 'helm_upgrade']
const HELM_MODELS: DeploymentModel[] = ['k3s_bare', 'k8s_edge', 'dc_ha']

function latestOpByTypes(operations: InfraOperation[], types: string[]): InfraOperation | undefined {
  return operations
    .filter(op => types.includes(op.operationType))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
}

function opToStatus(op: InfraOperation | undefined): { status: StageStatus; operationId?: string } {
  if (!op) return { status: 'not_started' }
  if (op.status === 'completed' || op.status === 'succeeded') return { status: 'completed', operationId: op.id }
  if (op.status === 'running' || op.status === 'pending') return { status: 'in_progress', operationId: op.id }
  if (op.status === 'failed') return { status: 'failed', operationId: op.id }
  return { status: 'not_started', operationId: op.id }
}

export function deriveLifecycleStages(
  environment: CloudEnvironment,
  operations: InfraOperation[],
  members: HostGroupMember[],
): LifecycleStage[] {
  const infraOp = latestOpByTypes(operations, INFRA_OP_TYPES)
  const helmOp = latestOpByTypes(operations, HELM_OP_TYPES)
  const infra = opToStatus(infraOp)
  const helm = opToStatus(helmOp)

  const onlineCount = members.filter(m => m.hostStatus === 'online').length
  const connectedStatus: StageStatus = onlineCount > 0 ? 'completed' : 'not_started'
  const connectedSummary = members.length > 0 ? `${onlineCount} / ${members.length}` : undefined

  // Enforce sequential dependencies: later stages cannot advance past earlier ones.
  const infraDone = infra.status === 'completed'

  const stages: LifecycleStage[] = [
    { id: 'created', status: 'completed' },
    { id: 'infrastructure', status: infra.status, operationId: infra.operationId },
  ]

  if (HELM_MODELS.includes(environment.deploymentModel)) {
    const runtimeStatus = infraDone ? helm.status : 'not_started'
    const runtimeOpId = infraDone ? helm.operationId : undefined
    stages.push({ id: 'runtime', status: runtimeStatus, operationId: runtimeOpId })
  }

  const prevDone = stages[stages.length - 1]?.status === 'completed'
  stages.push({
    id: 'connected',
    status: prevDone ? connectedStatus : 'not_started',
    summary: prevDone ? connectedSummary : undefined,
  })

  return stages
}
